import { Injectable } from "@angular/core";
import type { ParticleCollectionRepository } from "@partikelart/particles/data-access-core";
import {
	type CollectionMetadata,
	type CoordinateReference,
	Particle,
	type ParticleCollection,
	type ParticleMeasurements,
	Point2D,
	type Ring,
} from "@partikelart/particles/domain";
import type { CreateParticleCollectionDto, UpdateParticleCollectionDto } from "@partikelart/particles/dtos";
import { Observable, of, throwError } from "rxjs";
import demoCollections from "../assets/demo-particle-collections.json";

/**
 * The JSON projection of a domain type: `Point2D` extends `Array`, so it
 * round-trips as a plain `[x, y]` tuple. Everything else is already
 * JSON-native, which is why no hand-written serializer is needed.
 */
type AsJson<T> = T extends Point2D
	? readonly [number, number]
	: T extends readonly (infer U)[]
		? readonly AsJson<U>[]
		: T extends object
			? { readonly [K in keyof T]: AsJson<T[K]> }
			: T;

/** Shape of one entry in `assets/demo-particle-collections.json`. */
type DemoCollectionJson = {
	readonly id: string;
	readonly crs: CoordinateReference;
	readonly metadata: CollectionMetadata;
	/** `hash` is omitted — `Particle.fromContours` recomputes it from the geometry. */
	readonly particles: readonly AsJson<Pick<Particle, "exterior" | "holes" | "measurements">>[];
};

// `resolveJsonModule` widens tuples to `number[]`, hence the cast.
const seeds = demoCollections as unknown as readonly DemoCollectionJson[];

const point = ([x, y]: readonly [number, number]): Point2D => new Point2D(x, y);
const ring = (r: readonly (readonly [number, number])[]): Ring => r.map(point);

function reviveMeasurements(m: AsJson<ParticleMeasurements>): ParticleMeasurements {
	return {
		...m,
		feretMax: { ...m.feretMax, endpoints: [point(m.feretMax.endpoints[0]), point(m.feretMax.endpoints[1])] },
		feretMin: { ...m.feretMin, furthestPoint: point(m.feretMin.furthestPoint) },
		inCircle: { ...m.inCircle, center: point(m.inCircle.center) },
	};
}

function reviveCollection(json: DemoCollectionJson): ParticleCollection {
	return {
		type: "ParticleCollection",
		version: 1,
		crs: { ...json.crs },
		metadata: { ...json.metadata },
		particles: json.particles.map((p) =>
			Particle.fromContours(ring(p.exterior), p.holes.map(ring), reviveMeasurements(p.measurements)),
		),
	};
}

/**
 * Serves particle collections captured from real analysis runs, so features
 * such as the report can be built and demoed without the CV pipeline or a
 * device. Provide it in place of the real repository:
 *
 * ```ts
 * { provide: PARTICLE_COLLECTION_REPOSITORY, useClass: DemoParticleCollectionRepository }
 * ```
 *
 * To refresh the data, log a live collection and paste it into
 * `assets/demo-particle-collections.json` (adding an `id`):
 *
 * ```ts
 * console.log(JSON.stringify(await firstValueFrom(
 *   store.select(particleCollectionFeature.selectCollection))));
 * ```
 */
@Injectable({ providedIn: "root" })
export class DemoParticleCollectionRepository implements ParticleCollectionRepository {
	/** Ids of the seeded collections, in file order — use these to drive a report. */
	static readonly demoIds: readonly string[] = seeds.map((c) => c.id);

	/**
	 * Revives a seeded collection without going through DI or an Observable,
	 * for demo fixtures that need one synchronously. Each call returns a fresh
	 * copy, so callers can mutate it freely.
	 */
	static load(id: string): ParticleCollection | null {
		const seed = seeds.find((c) => c.id === id);
		return seed ? reviveCollection(seed) : null;
	}

	/** Collections written at runtime; shadows a seed of the same id. */
	private readonly written = new Map<string, ParticleCollection>();
	private readonly deleted = new Set<string>();

	getById(id: string): Observable<ParticleCollection | null> {
		if (this.deleted.has(id)) return of(null);
		return of(this.written.get(id) ?? DemoParticleCollectionRepository.load(id));
	}

	create(input: CreateParticleCollectionDto): Observable<ParticleCollection> {
		const collection: ParticleCollection = {
			type: "ParticleCollection",
			version: 1,
			crs: input.crs,
			metadata: input.metadata,
			particles: input.particles,
		};
		this.written.set(crypto.randomUUID(), collection);
		return of(collection);
	}

	update(id: string, changes: UpdateParticleCollectionDto): Observable<ParticleCollection> {
		const existing = this.written.get(id) ?? (this.deleted.has(id) ? null : DemoParticleCollectionRepository.load(id));
		if (!existing) return throwError(() => new Error(`Particle collection ${id} not found`));

		const updated: ParticleCollection = {
			...existing,
			crs: changes.crs ?? existing.crs,
			metadata: changes.metadata ?? existing.metadata,
			particles: changes.particles ?? existing.particles,
		};
		this.written.set(id, updated);
		return of(updated);
	}

	delete(id: string): Observable<void> {
		this.written.delete(id);
		this.deleted.add(id);
		return of(undefined);
	}
}
